import { useEffect, useState, type ReactNode } from 'react'
import { format, parseISO } from 'date-fns'
import { ArrowRight, Check, Mail, MailCheck, MailX, Minus, Send } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import type { ContentEntity } from '../../lib/api/content'
import { getReservationEmailLogs, resendReservationEmail, type EmailLogEntry } from '../../lib/api/client'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>

/* ── value helpers ── */
function ref(o: unknown): string {
  if (o == null) return ''
  if (typeof o !== 'object') return String(o)
  const r = o as { elements?: El; id?: string }
  const e = r.elements ?? {}
  return String(e.label ?? e.name ?? e.code ?? r.id ?? '')
}
function refList(o: unknown): string {
  if (Array.isArray(o)) return o.map(ref).filter(Boolean).join(', ')
  return ref(o)
}
function date(v: unknown): string {
  if (typeof v === 'number') {
    const d = new Date(v < 1e12 ? v * 1000 : v) // Unix seconds or ms
    return isNaN(d.getTime()) ? '—' : format(d, 'dd/MM/yyyy HH:mm')
  }
  if (!v || typeof v !== 'string') return '—'
  try {
    return format(parseISO(v), /T00:00(:00)?/.test(v) ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm')
  } catch {
    return String(v)
  }
}
function dateTime(v: unknown): string {
  if (!v || typeof v !== 'string') return '—'
  try {
    return format(parseISO(v), 'dd/MM/yyyy HH:mm')
  } catch {
    return String(v)
  }
}
function nights(a: unknown, l: unknown): string {
  if (typeof a !== 'string' || typeof l !== 'string') return ''
  try {
    const n = Math.round((+parseISO(l) - +parseISO(a)) / 86_400_000)
    return n > 0 ? `${n} giorn${n === 1 ? 'o' : 'i'}` : n === 0 ? 'in giornata' : ''
  } catch {
    return ''
  }
}
function money(c: unknown): string {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}
// "Incassato?" badge: only for bookings created after the capture-status fix went live
// (21/07/2026), so it never shows a pre-fix false "incassato" on older confirmed bookings.
const PAY_BADGE_SINCE = Date.parse('2026-07-21T00:00:00+02:00')
function createdMs(v: unknown): number | null {
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v
  if (typeof v === 'string') {
    const t = Date.parse(v)
    return Number.isNaN(t) ? null : t
  }
  return null
}
function txt(v: unknown): ReactNode {
  return v == null || v === '' ? <span className="text-faint">—</span> : String(v)
}
function address(o: unknown): string {
  if (!o || typeof o !== 'object') return ''
  // unwrap ref objects ({elements:{label}}) and coerce numeric parts (CAP/ZIP) — don't silently drop them
  return Object.values(o as El)
    .map((v) => (v && typeof v === 'object' ? ref(v) : v == null || v === '' ? '' : String(v)))
    .filter(Boolean)
    .join(', ')
}
const isYes = (v: unknown) => v === true || v === 'true'

/* ── channel / status chips (match the list) ── */
function channelBadge(src: string) {
  return { cls: 'text-slate border-line bg-paper', label: (src || '').toUpperCase() || 'DIRETTO' }
}
function statusChip(status: string) {
  const s = (status || '').toUpperCase()
  if (s === 'CANCELLED' || s === 'CANCELED') return { cls: 'bg-venetian-soft text-venetian', label: 'Annullata' }
  if (s === 'DRAFT') return { cls: 'bg-oro-soft text-oro-700', label: 'Bozza' }
  return { cls: 'bg-ok-soft text-ok', label: 'Confermata' }
}

/* ── layout atoms ── */
function YesNo({ v }: { v: unknown }) {
  const yes = isYes(v)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[11.5px] font-semibold',
        yes ? 'bg-ok-soft text-ok' : 'bg-paper text-faint ring-1 ring-line',
      )}
    >
      {yes ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      {yes ? 'Sì' : 'No'}
    </span>
  )
}
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line-soft py-[9px] last:border-0">
      <span className="shrink-0 text-[12.5px] text-muted">{label}</span>
      <span className="max-w-[64%] text-right text-[13px] font-medium text-coal [overflow-wrap:anywhere]">{value}</span>
    </div>
  )
}
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-4">
      <h3 className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{title}</h3>
      <div className="cv-panel px-4">{children}</div>
    </section>
  )
}
function Plate({ value }: { value: unknown }) {
  return value ? (
    <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[12px] font-semibold tracking-wider text-coal">
      {String(value)}
    </span>
  ) : (
    <span className="text-faint">—</span>
  )
}

/* ── confirmation email: status + resend ── */
type EmailMeta = { Icon: typeof Mail; bubble: string; text: string; title: string; sub: (l: EmailLogEntry | null) => string }
function emailStatusMeta(status: string): EmailMeta {
  const s = (status || '').toUpperCase()
  if (s === 'SENT')
    return { Icon: MailCheck, bubble: 'bg-ok-soft text-ok', text: 'text-ok', title: 'Email inviata', sub: (l) => (l ? `${l.toEmail}${l.sentTime ? ` · ${date(l.sentTime)}` : ''}` : '') }
  if (s === 'FAILED')
    return { Icon: MailX, bubble: 'bg-venetian-soft text-venetian', text: 'text-venetian', title: 'Invio fallito', sub: (l) => (l ? l.toEmail : '') }
  if (s === 'PENDING')
    return { Icon: Mail, bubble: 'bg-oro-soft text-oro-700', text: 'text-oro-700', title: 'In coda di invio', sub: (l) => (l ? l.toEmail : '') }
  return { Icon: Mail, bubble: 'bg-paper text-faint ring-1 ring-line', text: 'text-faint', title: 'Nessuna email', sub: () => 'Nessun invio registrato' }
}

function EmailSection({ code }: { code: string }) {
  const [logs, setLogs] = useState<EmailLogEntry[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [editing, setEditing] = useState(false)
  const [editEmail, setEditEmail] = useState('')

  const load = () => {
    if (!code) return
    getReservationEmailLogs(code)
      .then(setLogs)
      .catch(() => setLogs([]))
  }
  useEffect(() => {
    setLogs(null)
    setMsg(null)
    setEditing(false)
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const latest = logs && logs.length ? logs[0] : null
  const meta = emailStatusMeta(latest?.status ?? '')
  const failed = (latest?.status ?? '').toUpperCase() === 'FAILED'

  const onResend = async (overrideEmail?: string) => {
    setBusy(true)
    setMsg(null)
    try {
      const r = await resendReservationEmail(code, overrideEmail)
      setMsg({ ok: r.ok, text: r.message })
      if (r.ok && overrideEmail) setEditing(false)
      load()
    } catch {
      setMsg({ ok: false, text: "Errore durante l'invio" })
    } finally {
      setBusy(false)
    }
  }
  const onCorrect = () => {
    const e = editEmail.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      setMsg({ ok: false, text: 'Indirizzo email non valido' })
      return
    }
    onResend(e)
  }

  return (
    <Section title="Email di conferma">
      {/* status header */}
      <div className="flex items-center gap-3 pt-3 pb-1">
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', meta.bubble)}>
          <meta.Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13.5px] font-semibold text-coal">{meta.title}</span>
            {latest && latest.attempts > 1 && (
              <span className="rounded-full bg-paper px-1.5 py-px text-[10.5px] font-bold text-muted ring-1 ring-line">
                {latest.attempts} tentativi
              </span>
            )}
          </div>
          <div className="truncate text-[12px] text-muted">{logs === null ? 'Caricamento…' : meta.sub(latest)}</div>
        </div>
      </div>

      {/* error detail */}
      {failed && latest?.error && (
        <div className="mt-2 rounded-md bg-venetian-soft px-3 py-2 text-[11.5px] leading-relaxed text-venetian [overflow-wrap:anywhere]">
          {latest.error}
        </div>
      )}

      {/* history */}
      {logs && logs.length > 1 && (
        <details className="mt-2">
          <summary className="cursor-pointer select-none text-[12px] text-muted hover:text-coal">Storico invii ({logs.length})</summary>
          <div className="mt-1.5 space-y-1.5 border-l-2 border-line-soft pl-3">
            {logs.map((l) => {
              const m = emailStatusMeta(l.status)
              return (
                <div key={l.id} className="flex items-center justify-between gap-2 text-[12px]">
                  <span className="text-muted">{date(l.createdTime)}</span>
                  <span className={cn('inline-flex items-center gap-1 font-semibold', m.text)}>
                    <m.Icon className="h-3 w-3" />
                    {m.title}
                  </span>
                </div>
              )
            })}
          </div>
        </details>
      )}

      {/* action footer */}
      {editing ? (
        <div className="mt-2 space-y-2 border-t border-line-soft pt-3 pb-3">
          <label className="block text-[12px] font-semibold text-coal">Correggi l’indirizzo email del cliente</label>
          <input
            type="email"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            placeholder="email@cliente.it"
            autoComplete="off"
            className="h-9 w-full rounded-md border border-line bg-card px-3 text-[13px] text-coal outline-none focus:border-verdigris"
          />
          <p className="text-[11px] text-faint">L’indirizzo viene corretto sulla prenotazione e l’email re-inviata a questo nuovo indirizzo.</p>
          <div className="flex items-center justify-between gap-3">
            <span className={cn('text-[12px] [overflow-wrap:anywhere]', msg ? (msg.ok ? 'text-ok' : 'text-venetian') : 'text-faint')}>{msg?.text ?? ''}</span>
            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => { setEditing(false); setMsg(null) }} disabled={busy} className="rounded-lg border border-line bg-card px-3 py-2 text-[12.5px] font-semibold text-slate hover:border-faint disabled:opacity-50">
                Annulla
              </button>
              <button onClick={onCorrect} disabled={busy || !editEmail.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-coal px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-soft transition hover:opacity-90 disabled:opacity-50">
                <Send className={cn('h-3.5 w-3.5', busy && 'animate-pulse')} />
                {busy ? 'Invio…' : 'Correggi e reinvia'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-line-soft pt-3 pb-3">
          <span className={cn('text-[12px] [overflow-wrap:anywhere]', msg ? (msg.ok ? 'text-ok' : 'text-venetian') : 'text-faint')}>{msg?.text ?? ''}</span>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => { setEditing(true); setEditEmail(latest?.toEmail ?? ''); setMsg(null) }}
              disabled={busy}
              className="rounded-lg border border-line bg-card px-3 py-2 text-[12.5px] font-semibold text-slate hover:border-faint disabled:opacity-50"
            >
              Modifica indirizzo
            </button>
            <button
              onClick={() => onResend()}
              disabled={busy || !code}
              className="inline-flex items-center gap-1.5 rounded-lg bg-coal px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-soft transition hover:opacity-90 disabled:opacity-50"
            >
              <Send className={cn('h-3.5 w-3.5', busy && 'animate-pulse')} />
              {busy ? 'Invio…' : latest ? 'Reinvia email' : 'Invia email'}
            </button>
          </div>
        </div>
      )}
    </Section>
  )
}

export function ReservationDrawer({ row, onClose }: { row: ContentEntity | null; onClose: () => void }) {
  const el = (row?.elements ?? {}) as El
  const st = statusChip(String(el.status ?? ''))
  const ch = channelBadge(String(el.source ?? ''))
  const pay = (el.payment ?? null) as El | null
  const dur = nights(el.arrivalDate, el.leaveDate)

  const hasBilling =
    isYes(el.requireBilling) ||
    isYes(el.invoiceEmitted) ||
    isYes(el.invoiceAcquired) ||
    Boolean(el.businessName || el.businessVatId || el.businessSDI || el.businessEmail || el.businessPec || address(el.billingAddress))

  return (
    <Drawer
      open={Boolean(row)}
      onClose={onClose}
      tone="brand"
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[15px] font-bold text-white">{String(el.code ?? '—')}</span>
          <span className={`gv-chip ${st.cls}`}>
            <i className="h-1.5 w-1.5 rounded-full bg-current" />
            {st.label}
          </span>
          <span className={`gv-src ${ch.cls}`}>{ch.label}</span>
        </span>
      }
      subtitle={`${String(el.name ?? '')} ${String(el.lastName ?? '')} · ${ref(el.transport) || '—'}`}
      footer={
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] text-muted">Totale</span>
          <span className="font-display text-[20px] font-bold tnum text-coal">{money(el.totalPrice)}</span>
        </div>
      }
    >
      {/* itinerary hero */}
      <div className="mb-5 flex items-center justify-between gap-3 rounded-card border border-line bg-card px-4 py-3.5 shadow-soft">
        <div className="min-w-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[1.4px] text-muted">Arrivo</div>
          <div className="mt-0.5 font-mono text-[13.5px] font-semibold text-coal">{date(el.arrivalDate)}</div>
        </div>
        <div className="flex shrink-0 flex-col items-center px-1 text-faint">
          {dur && <span className="mb-0.5 whitespace-nowrap text-[10.5px] font-semibold text-muted">{dur}</span>}
          <ArrowRight className="h-4 w-4" />
        </div>
        <div className="min-w-0 text-right">
          <div className="text-[10.5px] font-bold uppercase tracking-[1.4px] text-muted">Partenza</div>
          <div className="mt-0.5 font-mono text-[13.5px] font-semibold text-coal">{date(el.leaveDate)}</div>
        </div>
      </div>

      <Section title="Soggiorno">
        <Row label="Origine" value={<span className={`gv-src ${ch.cls}`}>{ch.label}</span>} />
        <Row label="Tipo di veicolo" value={ref(el.transport) || '—'} />
        <Row label="Targa" value={<Plate value={el.licencePlate} />} />
        <Row label="Servizi" value={refList(el.services) || '—'} />
        <Row label="Convenzione" value={ref(el.convention) || '—'} />
        <Row label="Coupon" value={txt(el.coupon)} />
        <Row label="Rif. esterno" value={txt(el.sourceReference)} />
        <Row label="Codice cancellazione" value={<span className="font-mono text-[12px]">{txt(el.cancellationCode)}</span>} />
        <Row label="Data inserimento" value={date(el.creationTime)} />
      </Section>

      <Section title="Acquisizione">
        <Row label="Prenotazione acquisita" value={<YesNo v={el.acquired} />} />
        <Row label="In data" value={isYes(el.acquired) ? date(el.acquisitionDate) : '—'} />
      </Section>

      <EmailSection code={String(el.code ?? '')} />

      <Section title="Cliente">
        <Row label="Nome" value={txt(el.name)} />
        <Row label="Cognome" value={txt(el.lastName)} />
        <Row label="Email" value={txt(el.email)} />
        <Row label="Telefono" value={txt(el.phoneNumber)} />
        <Row label="Nazionalità" value={txt(el.country)} />
      </Section>

      <Section title="Pagamento">
        {(() => {
          const cms = createdMs(el.creationTime)
          const confirmed = /CONFIRM/i.test(String(el.status ?? ''))
          if (!confirmed || cms == null || cms < PAY_BADGE_SINCE) return null
          const s = String(pay?.status ?? '').toUpperCase()
          const refunded = /REFUND|RIMBORS/.test(s)
          const paid = /COMPLET|PAID|SUCCE/.test(s)
          const cls = refunded ? 'bg-oro-soft text-oro-700' : paid ? 'bg-ok-soft text-ok' : 'bg-venetian-soft text-venetian'
          const label = refunded ? 'Rimborsato' : paid ? `Incassato · ${money(el.totalPrice)}` : 'Non incassato'
          return (
            <div className="mb-2 flex justify-center">
              <span className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-bold', cls)}>{label}</span>
            </div>
          )
        })()}
        <Row label="Metodo di pagamento" value={ref(el.paymentMode) || '—'} />
        <Row label="Totale" value={<span className="font-mono">{money(el.totalPrice)}</span>} />
        {pay && (
          <>
            <Row
              label="Stato pagamento"
              value={
                <span className={cn('font-semibold', /COMPLET|PAID|SUCCE/i.test(String(pay.status)) ? 'text-ok' : 'text-muted')}>
                  {txt(pay.status)}
                </span>
              }
            />
            <Row label="ID ordine" value={<span className="font-mono text-[12px]">{txt(pay.orderId)}</span>} />
            <Row label="ID transazione" value={<span className="font-mono text-[12px]">{txt(pay.transactionId)}</span>} />
            <Row label="Data pagamento" value={dateTime(pay.timestamp)} />
            {pay.transactionData != null &&
              String(pay.transactionData) !== '' &&
              String(pay.transactionData) !== 'null' && (
                <details className="border-t border-line-soft py-2">
                  <summary className="cursor-pointer select-none text-[12.5px] text-muted hover:text-coal">
                    Dettagli tecnici
                  </summary>
                  <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap rounded-md bg-paper p-2.5 font-mono text-[11px] leading-relaxed text-slate [overflow-wrap:anywhere]">
                    {String(pay.transactionData)}
                  </pre>
                </details>
              )}
          </>
        )}
      </Section>

      {hasBilling && (
        <Section title="Fatturazione">
          <Row label="Richiesta fattura" value={<YesNo v={el.requireBilling} />} />
          <Row label="Tipo" value={el.billingType === 'azienda' ? 'Azienda / P.IVA' : el.billingType === 'privato' ? 'Privato' : '—'} />
          <Row label="Ragione sociale / Cognome nome" value={txt(el.businessName)} />
          <Row label="Codice fiscale" value={txt(el.codiceFiscale)} />
          <Row label="Partita IVA" value={txt(el.businessVatId)} />
          <Row label="Codice SDI" value={txt(el.businessSDI)} />
          <Row label="Email aziendale" value={txt(el.businessEmail)} />
          <Row label="PEC" value={txt(el.businessPec)} />
          <Row label="Referente" value={txt(el.referente)} />
          <Row label="Pubblica Amministrazione" value={<YesNo v={el.isPublicAdminitration ?? el.isPublicAdministration} />} />
          <Row label="Indirizzo" value={address(el.billingAddress) || '—'} />
          <Row label="Fattura acquisita" value={<YesNo v={el.invoiceAcquired} />} />
          {isYes(el.invoiceAcquired) && <Row label="Acquisita il" value={date(el.invoiceAcquisitionDate)} />}
          <Row label="Fattura emessa" value={<YesNo v={el.invoiceEmitted} />} />
          {isYes(el.invoiceEmitted) && <Row label="Emessa il" value={date(el.invoiceEmissionDate)} />}
        </Section>
      )}

      <Section title="Consensi privacy">
        <Row label="Trattamento dei dati personali" value={<YesNo v={el.privacy} />} />
        <Row label="Marketing e promozioni" value={<YesNo v={el.commercialPrivacy} />} />
        <Row label="Studi e ricerche di mercato" value={<YesNo v={el.researchPrivacy} />} />
      </Section>

      {typeof el.qrCode === 'string' && el.qrCode.startsWith('data:image') && (
        <Section title="QR-Code">
          <div className="flex justify-center py-3">
            <img src={el.qrCode} alt="QR" className="h-32 w-32 rounded-md border border-line bg-white p-1" />
          </div>
        </Section>
      )}
    </Drawer>
  )
}
